// src/components/MentionRenderer.tsx
import React, { useEffect, useState } from 'react';

// 🔁 lazy memo do mentionsService
let __mentionsMod: any;
async function getMentions() {
  if (!__mentionsMod) __mentionsMod = await import('../services/mentionsService');
  return __mentionsMod;
}

interface MentionRendererProps {
  texto: string;
  className?: string;
  modoEscuro?: boolean;
}

export default function MentionRenderer({ 
  texto, 
  className = "",
  modoEscuro = false 
}: MentionRendererProps) {
  const [partes, setPartes] = useState<Array<{tipo:'mencao'|'texto'; conteudo:string}>>([{ tipo:'texto', conteudo: texto }]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { extrairMencoesParaRenderizacao } = await getMentions();
        const p = extrairMencoesParaRenderizacao(texto) || [{ tipo:'texto', conteudo: texto }];
        if (alive) setPartes(p);
      } catch {
        if (alive) setPartes([{ tipo:'texto', conteudo: texto }]);
      }
    })();
    return () => { alive = false; };
  }, [texto]);

  return (
    <span className={`${className}`}>
      {partes.map((parte, index) => {
        if (parte.tipo === 'mencao') {
          const primeiroNome = parte.conteudo.split(' ')[0];
          return (
            <span
              key={index}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md text-sm font-medium transition-colors ${
                modoEscuro
                  ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}
              title={`Usuário mencionado: ${parte.conteudo}`}
            >
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                modoEscuro 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-500 text-white'
              }`}>
                {primeiroNome.charAt(0).toUpperCase()}
              </span>
              @{primeiroNome}
            </span>
          );
        } else {
          // Renderizar texto normal, preservando quebras de linha
          return (
            <span key={index} className="whitespace-pre-wrap">
              {parte.conteudo}
            </span>
          );
        }
      })}
    </span>
  );
}

// Componente auxiliar para preview rápido de menções
export function MentionPreview({ 
  mencoes, 
  maxVisible = 3,
  modoEscuro = false 
}: { 
  mencoes: string[], 
  maxVisible?: number,
  modoEscuro?: boolean 
}) {
  if (mencoes.length === 0) return null;

  const visibles = mencoes.slice(0, maxVisible);
  const resto = mencoes.length - maxVisible;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className={`text-xs ${
        modoEscuro ? 'text-gray-400' : 'text-gray-500'
      }`}>
        Mencionou:
      </span>
      {visibles.map((nome, index) => {
        const primeiroNome = nome.split(' ')[0];
        return (
          <span
            key={index}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
              modoEscuro
                ? 'bg-blue-900/20 text-blue-400 border border-blue-800/30'
                : 'bg-blue-50 text-blue-600 border border-blue-200/50'
            }`}
            title={nome} // ✅ Mostrar nome completo no hover
          >
            <span className={`w-3 h-3 rounded-full flex items-center justify-center text-xs font-bold ${
              modoEscuro 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-500 text-white'
            }`}>
              {primeiroNome.charAt(0).toUpperCase()}
            </span>
            {primeiroNome} {/* ✅ Mostrar apenas primeiro nome */}
          </span>
        );
      })}
      {resto > 0 && (
        <span className={`text-xs ${
          modoEscuro ? 'text-gray-400' : 'text-gray-500'
        }`}>
          +{resto} outros
        </span>
      )}
    </div>
  );
}

// Hook para detectar se o usuário atual foi mencionado
export function useIsMentioned(mencoes: string[], currentUserName?: string): boolean {
  if (!currentUserName || !mencoes.length) return false;
  
  const primeiroNomeAtual = currentUserName.split(' ')[0].toLowerCase();
  
  return mencoes.some(nome => {
    const primeiroNomeMencao = nome.split(' ')[0].toLowerCase();
    return primeiroNomeMencao === primeiroNomeAtual;
  });
}

// Componente para destacar comentários onde o usuário foi mencionado
export function CommentMentionHighlight({ 
  children, 
  isMentioned, 
  modoEscuro = false 
}: { 
  children: React.ReactNode, 
  isMentioned: boolean,
  modoEscuro?: boolean 
}) {
  if (!isMentioned) return <>{children}</>;

  return (
    <div className={`relative ${
      modoEscuro
        ? 'bg-yellow-900/10 border-l-4 border-yellow-600/50'
        : 'bg-yellow-50 border-l-4 border-yellow-400'
    } pl-3 py-1 rounded-r-md`}>
      <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold ${
        modoEscuro
          ? 'bg-yellow-800/30 text-yellow-400'
          : 'bg-yellow-200 text-yellow-800'
      }`}>
        📌 Você foi mencionado
      </div>
      {children}
    </div>
  );
}
